/**
 * `timeline` → **Customize Timeline column** — rebuilt 1:1 from the capture
 * (`discovery/ui-reference/editors/timeline/dialog-settings.{png,json}`).
 *
 * Measured, in order, on a 320 × 364 card: the title *Timeline column settings*; the checkbox
 * **Show week number** (147 × 20 at y 339); the checkbox **Show 'Set as milestone'** (172 × 20 at
 * y 375); a 288 × 1 divider (y 403); the checkbox **Include weekends** (136 × 20 at y 420) with
 * monday's indented subtitle *"Allow selection of weekend days and include them in items
 * duration."* and a *Learn more* link; a second divider (y 510); and the **Sync column to
 * calendar** block with a *Google Calendar* row and an *Other calendars* row, each with a `Sync`
 * button.
 *
 * The calendar sync is monday's integration surface, not a column setting — it writes no
 * `settings_str` key and this product has no Google integration — so it is **absent, not
 * disabled** (B1 §3.2). The *Learn more* link is likewise monday's help centre and is not
 * rendered as a dead link.
 *
 * Every key here is `timelineSettingsSchema`'s: `show_week_number`, `show_set_as_milestone` and
 * `show_weekends`. `visualization_type` is readable and **unwritable** (q334), so the milestone
 * *visualisation* the card asks about is not offered — only monday's own on/off switch is.
 */
import type { TimelineSettings } from '@elizmonday/shared';
import {
  HideSummaryField,
  SettingsCheckbox,
  SettingsDivider,
  SettingsForm,
  useSettingsDraft,
} from '../settings-kit';
import type { ColumnSettingsProps } from '../types';

export function TimelineSettingsDialog({
  settings,
  onCommit,
  onCancel,
}: ColumnSettingsProps<TimelineSettings>) {
  const [draft, patch] = useSettingsDraft<TimelineSettings>(settings);
  return (
    <SettingsForm
      title="Timeline column settings"
      testId="timeline-settings"
      onSave={() => onCommit(draft)}
      onCancel={onCancel}
    >
      <SettingsCheckbox
        label="Show week number"
        testId="timeline-week-number"
        checked={draft.show_week_number ?? false}
        onChange={(next) => patch({ show_week_number: next })}
      />
      <SettingsCheckbox
        label="Show 'Set as milestone'"
        testId="timeline-milestone"
        checked={draft.show_set_as_milestone ?? false}
        onChange={(next) => patch({ show_set_as_milestone: next })}
      />
      <SettingsDivider />
      <SettingsCheckbox
        label="Include weekends"
        hint="Allow selection of weekend days and include them in items duration."
        testId="timeline-weekends"
        checked={draft.show_weekends ?? false}
        onChange={(next) => patch({ show_weekends: next })}
      />
      <HideSummaryField
        testId="timeline-hide-footer"
        checked={draft.hide_footer ?? false}
        onChange={(next) => patch({ hide_footer: next })}
      />
    </SettingsForm>
  );
}
